/**
 * offlineReviews — localStorage-backed queue for flashcard grades the
 * student gave while offline, plus a cached deck so review still works
 * with no network.
 *
 * Storage keys:
 *   istudypro_pending_reviews_v1 → Array<{
 *     client_review_id: string (uuid),
 *     byte_id: string,
 *     quality: 0..5,
 *     reviewed_at: string (ISO),
 *   }>
 *   istudypro_offline_deck_v1 → { cards: Array, cached_at: string }
 *   istudypro_last_sync_v1 → string (ISO)
 *
 * The queue is append-only on the client; entries are removed only after
 * the server confirms a `synced` or `skipped_duplicate` status. A
 * `card_not_found` failure also drops the entry (the card was deleted
 * server-side; the grade is no longer applicable).
 */
import axios from 'axios';

const QUEUE_KEY = 'istudypro_pending_reviews_v1';
const DECK_KEY = 'istudypro_offline_deck_v1';
const LAST_SYNC_KEY = 'istudypro_last_sync_v1';

const safeParse = (raw, fallback) => {
  if (!raw) return fallback;
  try { return JSON.parse(raw); } catch { return fallback; }
};

const safeWrite = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {
    // QuotaExceededError or Safari private mode — best-effort only.
  }
};

// Lightweight RFC4122-ish uuid (the server only needs uniqueness +
// stability per grade; this avoids pulling in the uuid package).
export const newClientReviewId = () => {
  const rand = (n) => Math.random().toString(16).slice(2, 2 + n);
  return `${rand(8)}-${rand(4)}-${rand(4)}-${rand(4)}-${rand(12)}`;
};

export const getPendingReviews = () => {
  const arr = safeParse(localStorage.getItem(QUEUE_KEY), []);
  return Array.isArray(arr) ? arr : [];
};

export const queueOfflineReview = ({ byte_id, quality }) => {
  const entry = {
    client_review_id: newClientReviewId(),
    byte_id,
    quality,
    reviewed_at: new Date().toISOString(),
  };
  const queue = getPendingReviews();
  queue.push(entry);
  safeWrite(QUEUE_KEY, queue);
  return entry;
};

export const removeSyncedReviews = (clientReviewIds) => {
  const ids = new Set(clientReviewIds || []);
  const remaining = getPendingReviews().filter(
    (r) => !ids.has(r.client_review_id)
  );
  safeWrite(QUEUE_KEY, remaining);
  return remaining.length;
};

export const cacheOfflineDeck = (cards, cachedAt) => {
  safeWrite(DECK_KEY, {
    cards: Array.isArray(cards) ? cards : [],
    cached_at: cachedAt || new Date().toISOString(),
  });
};

export const getCachedDeck = () => {
  return safeParse(localStorage.getItem(DECK_KEY), null);
};

export const getLastSyncAt = () => {
  return localStorage.getItem(LAST_SYNC_KEY) || null;
};

const setLastSyncAt = (iso) => {
  try { localStorage.setItem(LAST_SYNC_KEY, iso); } catch { /* ignore */ }
};

/**
 * Push the entire local queue to the server. Returns
 * { synced, skipped, failed, dropped } where `dropped` is the count of
 * entries removed from localStorage (synced + skipped_duplicate +
 * card_not_found). On network failure, the queue is preserved untouched
 * so the next online attempt retries the same items.
 */
export const syncPendingReviews = async (apiBase) => {
  const queue = getPendingReviews();
  if (queue.length === 0) {
    return { synced: 0, skipped: 0, failed: 0, dropped: 0, attempted: 0 };
  }

  const { data } = await axios.post(`${apiBase}/srs/sync-offline`, {
    reviews: queue,
  });

  // Drop everything the server resolved (synced + duplicate + not-found)
  // — those don't need to be retried. Genuine transient failures (none
  // emitted by the current backend, but defended-against here) stay in
  // the queue.
  const resolvedIds = (data.results || [])
    .filter((r) => [
      'synced',
      'skipped_duplicate',
      'card_not_found',
    ].includes(r.status))
    .map((r) => r.client_review_id);

  removeSyncedReviews(resolvedIds);
  setLastSyncAt(new Date().toISOString());

  return {
    synced: data.synced || 0,
    skipped: data.skipped || 0,
    failed: data.failed || 0,
    dropped: resolvedIds.length,
    attempted: queue.length,
    results: data.results || [],
  };
};
